import {ShowPageScopeCollector} from '@frontend/group/modules/scope-collectors/show-page-collector';
import {isDeleteConfirmed} from '@frontend/lib/utils/is-delete-confirmed';
import {unFocusObjects} from '@frontend/utils';
import {deleteBaseLayer} from '@/common/preview/common-helper';
import {deleteAdditionalBackgroundThemeImageField} from '@frontend/group/modules/additional-fields/deleting';
import {deleteDisplayBuilderBackgroundLayer} from '@frontend/group/modules/display-builder/delete-background-layer';

const deletingCanvases = new WeakSet();
const additionalCollections = {
  add_text_: 'additionalTexts',
  add_multi_line_text_: 'additionalMultiLineTexts',
  add_rectangle_: 'additionalRectangles',
  add_circle_: 'additionalCircles',
  add_line_: 'additionalLines',
  add_safe_zone_: 'additionalSafeZones',
  add_cut_line_: 'additionalCutLines',
  toc_line_: 'tocLines',
};

function releaseSelection(target, canvas, canvasData) {
  const objects = target.type === 'activeSelection' ? [...target.getObjects()] : [target];
  // Fabric restores absolute member coordinates when discarding ActiveSelection.
  // Removing a member first leaves its selection controls pointing at a missing canvas.
  canvas.discardActiveObject();

  return objects.flatMap(object => {
    if (object.type !== 'group' || !object.id?.startsWith('group_')) return [object];

    const members = [...object.getObjects()];
    object.toActiveSelection();
    canvas.discardActiveObject();
    canvasData.groupFields.remove(object.id);
    return members.flatMap(member => releaseSelection(member, canvas, canvasData));
  });
}

function resolveField(object, preview) {
  const fieldId = object.templateField?.id ?? object.fieldId;
  return preview.template.fields.find(field => (
    (fieldId != null && String(field.id) === String(fieldId))
    || (object.element_id && field.element_id === object.element_id)
  ));
}

function removeAdditionalData(object, preview, scope) {
  const elementId = object.element_id;
  const instanceId = preview.id;
  const canvasData = scope.canvas_data[instanceId];
  const matches = key => key === elementId || key.startsWith(`${elementId}_`);

  scope.queueAdditionalFieldDeletionForSave({
    instanceId,
    elementId,
    customerId: preview.template.customer_id,
  });
  for (const data of new Set([preview.data, canvasData._data])) {
    Object.keys(data).filter(matches).forEach(key => delete data[key]);
  }
  for (let i = scope.layoutChanges.length - 1; i >= 0; i--) {
    const change = scope.layoutChanges[i];
    if (change.instance_id === instanceId && matches(change.key)) scope.layoutChanges.splice(i, 1);
  }
  for (const collection of [...Object.values(additionalCollections), 'additionalSmartObjects']) {
    if (Array.isArray(canvasData[collection])) {
      canvasData[collection] = canvasData[collection].filter(id => id !== elementId);
    }
  }
  preview.canvas.remove(object);
}

function removeArrayEntry(preview, scope, key, index, splice = true) {
  const values = [...(preview.data[key] || [])];
  if (splice) values.splice(index, 1);
  else values[index] = '';
  scope.updateLayoutChanges(preview.id, key, values);
}

async function deleteObject(object, preview, scope) {
  const canvas = preview.canvas;
  const canvasData = scope.canvas_data[preview.id];
  const id = object.id || '';
  const elementId = object.element_id || '';
  const field = resolveField(object, preview);

  if (id.startsWith('add_bk_theme_img_')) {
    if (window.isDisplayBuilder) deleteDisplayBuilderBackgroundLayer(object);
    else deleteAdditionalBackgroundThemeImageField(object);
    return;
  }

  if (object.inputDataId && id.startsWith('background_theme_imgs_multiple_')) {
    canvasData.backgroundThemeImageMultipleList.remove(object.inputDataId, canvas);
    scope.updateLayoutChanges(preview.id, 'background_theme_image_multiple',
      canvasData.backgroundThemeImageMultipleList.getAllInputDatasToJson());
    return;
  }

  if (id.startsWith('add_icons_') || id.startsWith('icons_')) {
    const key = id.startsWith('add_icons_') ? 'add_icons' : 'icons';
    const prefix = key === 'icons' ? 'icons_' : 'add_icons_';
    const values = preview.data[key] || [];
    const index = values.findIndex(group => `${prefix}${group.id}` === id);
    if (index === -1) throw new Error(`Cannot find icon layer ${id}`);
    removeArrayEntry(preview, scope, key, index);
    canvas.remove(object);
    return;
  }

  if (id.startsWith('add_html_table_')) {
    const index = Number(id.slice('add_html_table_'.length));
    removeArrayEntry(preview, scope, 'add_html_tables', index);
    if (preview.data.add_html_tables_visible) {
      removeArrayEntry(preview, scope, 'add_html_tables_visible', index);
    }
    canvas.remove(object);
    canvas.getObjects().forEach(item => {
      const match = /^add_html_table_(\d+)$/.exec(item.id || '');
      if (match && Number(match[1]) > index) {
        item.id = `add_html_table_${Number(match[1]) - 1}`;
        item.element_id = item.id;
      }
    });
    return;
  }

  if (id.startsWith('html_')) {
    const index = Number(id.slice('html_'.length));
    removeArrayEntry(preview, scope, 'html_tables', index, false);
    const removed = {...preview.data.removed_html_tables, [index]: true};
    scope.updateLayoutChanges(preview.id, 'removed_html_tables', removed);
  }

  const isSmartObject = elementId.startsWith('smart_object_');
  if (isSmartObject) {
    for (const memberId of [...(object.members || [])]) {
      object.removeMember(memberId, {preventChangesUpdate: true});
    }
    if (field) {
      object.templateField = field;
      deleteBaseLayer.call(object);
    }
    // Leaving *_options behind would recreate a deleted base Smart Object as an additional one.
    removeAdditionalData(object, preview, scope);
    return;
  }

  if (Object.keys(additionalCollections).some(prefix => elementId.startsWith(prefix))) {
    removeAdditionalData(object, preview, scope);
    return;
  }

  if (!field) throw new Error(`Unsupported layer ${object.name || id}`);

  // These fields maintain indexed image arrays as well as deleted_layers.
  // Resolve the live object immediately before use because deletion can redraw siblings.
  if ((id.startsWith('image_') || id.startsWith('bk_theme_img_'))
    && typeof object.deleteLayer === 'function') {
    await object.deleteLayer({updateAllTemplates: false});
    canvas.remove(object);
    return;
  }

  object.templateField = field;
  deleteBaseLayer.call(object);
}

function deletionIndex(object) {
  // Delete indexed families from the end: each removal can shift/rebuild the remaining layers.
  const match = /^(?:add_bk_theme_img_|bk_theme_img_|image_|add_html_table_)(\d+)$/.exec(object.id || '');
  return match ? Number(match[1]) : -1;
}

export async function deleteSelectedLayers(target, {confirm = true} = {}) {
  const canvas = target?.canvas;
  if (!canvas || deletingCanvases.has(canvas)) return false;
  deletingCanvases.add(canvas);

  let deleted = 0;
  let failed = 0;
  let started = false;
  const scope = ShowPageScopeCollector.get([
    'previews', 'canvas_data', 'layoutChanges', 'updateLayoutChanges',
    'queueAdditionalFieldDeletionForSave', 'reorderObjectCanvas', 'saveLayout',
    'syncCurrentProductImageWithStore',
  ]);

  try {
    const preview = scope.previews.find(item => item.canvas === canvas);
    if (!preview) return false;
    if (confirm && !await isDeleteConfirmed(true)) return false;

    started = true;
    $('.loadingOverlay-text').text('Deleting selected fields...');
    $('.loadingOverlay').show();

    const objects = releaseSelection(target, canvas, scope.canvas_data[preview.id]);
    const descriptors = objects.map(object => ({
      object,
      id: object.id,
      fieldId: object.templateField?.id ?? object.fieldId,
      elementId: object.element_id,
    })).sort((a, b) => deletionIndex(b.object) - deletionIndex(a.object));

    for (const descriptor of descriptors) {
      const object = canvas.getObjects().find(item => (
        item === descriptor.object
        || (descriptor.fieldId != null && String(item.fieldId ?? item.templateField?.id) === String(descriptor.fieldId)
          && item.id === descriptor.id)
        || (descriptor.elementId && item.element_id === descriptor.elementId && item.id === descriptor.id)
      ));
      if (!object) continue;
      try {
        await deleteObject(object, preview, scope);
        deleted++;
      } catch (error) {
        failed++;
        console.error('Error deleting selected field:', descriptor.id, error);
      }
    }

    scope.syncCurrentProductImageWithStore(canvas);
    scope.reorderObjectCanvas(canvas);
    canvas.requestRenderAll();
    unFocusObjects();

    const saved = deleted > 0 ? await scope.saveLayout() : true;
    if (failed) toastr.error(`Deleted ${deleted} fields; ${failed} fields could not be deleted.`);
    return saved && !failed;
  } catch (error) {
    console.error('Error deleting selected fields:', error);
    toastr.error('Could not finish deleting the selected fields. Some fields may have been deleted.');
    return false;
  } finally {
    if (started) $('.loadingOverlay').hide();
    deletingCanvases.delete(canvas);
  }
}
